import { Icon } from "@iconify/react";
import React, { useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { generateEventId, trackLeadSubmission } from "../../utils/analytics";

/* ---------------------------------------------------------------
   Clinic hours: 11:00 AM - 4:00 PM.
   Booking slots offered to patients: 11:00 AM - 3:30 PM, in
   10-minute intervals.
   `value` is stored in 24-hour "HH:mm" (matches backend),
   `label` is the human-readable 12-hour text in the dropdown.
----------------------------------------------------------------- */
const APPOINTMENT_TIME_SLOTS = [
  { value: "11:00", label: "11:00 AM" },
  { value: "11:10", label: "11:10 AM" },
  { value: "11:20", label: "11:20 AM" },
  { value: "11:30", label: "11:30 AM" },
  { value: "11:40", label: "11:40 AM" },
  { value: "11:50", label: "11:50 AM" },
  { value: "12:00", label: "12:00 PM" },
  { value: "12:10", label: "12:10 PM" },
  { value: "12:20", label: "12:20 PM" },
  { value: "12:30", label: "12:30 PM" },
  { value: "12:40", label: "12:40 PM" },
  { value: "12:50", label: "12:50 PM" },
  { value: "13:00", label: "1:00 PM" },
  { value: "13:10", label: "1:10 PM" },
  { value: "13:20", label: "1:20 PM" },
  { value: "13:30", label: "1:30 PM" },
  { value: "13:40", label: "1:40 PM" },
  { value: "13:50", label: "1:50 PM" },
  { value: "14:00", label: "2:00 PM" },
  { value: "14:10", label: "2:10 PM" },
  { value: "14:20", label: "2:20 PM" },
  { value: "14:30", label: "2:30 PM" },
  { value: "14:40", label: "2:40 PM" },
  { value: "14:50", label: "2:50 PM" },
  { value: "15:00", label: "3:00 PM" },
  { value: "15:10", label: "3:10 PM" },
  { value: "15:20", label: "3:20 PM" },
  { value: "15:30", label: "3:30 PM" },
];

export default function AppointmentForm() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name:   "",
    phone:  "",
    date:   "",
    time:   "",
    email:  "",
    visit:  "",
    record: "",
  });

  // Generic handler for text / email / record inputs
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // DatePicker onChange - stores date as "YYYY-MM-DD" (timezone-safe)
  const handleDateChange = (date) => {
    if (date) {
      const offsetDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      setFormData((prev) => ({
        ...prev,
        date: offsetDate.toISOString().slice(0, 10),
      }));
    } else {
      setFormData((prev) => ({ ...prev, date: "" }));
    }
  };

  // Time select onChange
  const handleTimeChange = (event) => {
    setFormData((prev) => ({ ...prev, time: event.target.value }));
  };

  // Radio button onChange (Reason for Visit)
  const handleRadioChange = (event) => {
    setFormData((prev) => ({ ...prev, visit: event.target.value }));
  };

  // Form Submit
  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    // Generate unique event ID for Meta Pixel + CAPI deduplication
    const eventId = generateEventId();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/appointment`,
        {
          ...formData,
          event_id:         eventId,
          form_type:        "detailed_appointment",
          event_source_url: window.location.href,
        }
      );

      if (response.status === 200) {
        Swal.fire({
          icon:  "success",
          title: "Appointment Confirmed!",
          text:  "Thank you for choosing Pluro Jabalpur IVF Center!",
        });

        // Push to dataLayer for GTM -> Meta Pixel deduplication
        trackLeadSubmission("detailed_appointment", eventId, {
          name:  formData.name,
          phone: formData.phone,
          email: formData.email,
        });

        // Reset form
        setFormData({
          name:   "",
          phone:  "",
          record: "",
          email:  "",
          date:   "",
          time:   "",
          visit:  "",
        });
      }
    } catch (error) {
      console.error("Appointment form error:", error);
      Swal.fire({
        icon:  "error",
        title: "Error",
        text:  "Error occurred while sending data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      action="#"
      method="POST"
      id="appointment-form"
      className="row"
      onSubmit={onSubmit}
    >
      {/* Full Name */}
      <div className="col-lg-6">
        <label className="cs_input_label cs_heading_color">Full Name</label>
        <input
          type="text"
          className="cs_form_field"
          placeholder="Your Full Name"
          id="name"
          name="name"
          onChange={handleInputChange}
          value={formData.name}
          required
        />
        <div className="cs_height_42 cs_height_xl_25" />
      </div>

      {/* Phone Number */}
      <div className="col-lg-6">
        <label className="cs_input_label cs_heading_color">Phone Number</label>
        <PhoneInput
          country={"in"}
          enableSearch={true}
          value={formData.phone}
          onChange={(phone) =>
            setFormData((prev) => ({ ...prev, phone: "+" + phone }))
          }
          inputClass="cs_form_field"
          inputStyle={{
            width:        "100%",
            height:       "56px",
            borderRadius: "30px",
            paddingLeft:  "48px",
          }}
          buttonStyle={{
            borderRadius:    "30px 0 0 30px",
            backgroundColor: "transparent",
            border:          "none",
            paddingLeft:     "10px",
          }}
          containerStyle={{ width: "100%" }}
        />
        <div className="cs_height_42 cs_height_xl_25" />
      </div>

      {/* Email */}
      <div className="col-lg-12">
        <label className="cs_input_label cs_heading_color">
          Enter Your Email ID
        </label>
        <input
          type="email"
          className="cs_form_field"
          id="email"
          name="email"
          placeholder="example@email.com"
          onChange={handleInputChange}
          value={formData.email}
          required
        />
        <div className="cs_height_42 cs_height_xl_25" />
      </div>

      {/* Medical Record Number (optional) */}
      <div className="col-lg-12">
        <label className="cs_input_label cs_heading_color">
          If you have medical record number
        </label>
        <input
          type="text"
          className="cs_form_field"
          placeholder="782DF45"
          name="record"
          onChange={handleInputChange}
          value={formData.record}
        />
        <div className="cs_height_42 cs_height_xl_25" />
      </div>

      {/* Preferred Date */}
      <div className="col-lg-6">
        <label className="cs_input_label cs_heading_color">
          Preferred Date
        </label>
        <div className="cs_with_icon_input">
          <DatePicker
            className="cs_form_field"
            dateFormat="dd/MM/yyyy"
            minDate={new Date()}
            isClearable
            placeholderText="dd/mm/yyyy"
            selected={formData.date ? new Date(formData.date) : null}
            onChange={handleDateChange}
            name="date"
            id="date"
            required
          />
          <i>
            <Icon icon="fa6-solid:calendar-days" />
          </i>
        </div>
        <div className="cs_height_42 cs_height_xl_25" />
      </div>

      {/* Preferred Time */}
      <div className="col-lg-6">
        <label className="cs_input_label cs_heading_color">
          Preferred Time
        </label>
        <div className="cs_with_icon_input">
          <select
            className="cs_form_field"
            onChange={handleTimeChange}
            value={formData.time}
            name="time"
            id="time"
            required
            style={{
              appearance:       "none",
              WebkitAppearance: "none",
              MozAppearance:    "none",
              backgroundColor:  "transparent",
            }}
          >
            <option value="" disabled>
              Select a time slot
            </option>
            {APPOINTMENT_TIME_SLOTS.map((slot) => (
              <option key={slot.value} value={slot.value}>
                {slot.label}
              </option>
            ))}
          </select>
          <i>
            <Icon icon="fa6-regular:clock" />
          </i>
        </div>
        <small style={{ display: "block", marginTop: "6px", color: "#666666" }}>
          Appointments available 11:00 AM to 3:30 PM (Clinic hours: 11 AM to 4 PM)
        </small>
        <div className="cs_height_42 cs_height_xl_25" />
      </div>

      {/* Reason for Visit */}
      <div className="col-lg-12">
        <label className="cs_input_label cs_heading_color">
          Reason for Visit
        </label>
        <div className="cs_radio_group">
          <div className="cs_radio_wrap">
            <input
              className="cs_radio_input"
              type="radio"
              name="visit"
              id="routineCheckup"
              value="routineCheckup"
              onChange={handleRadioChange}
              checked={formData.visit === "routineCheckup"}
            />
            <label className="cs_radio_label" htmlFor="routineCheckup">
              Routine Checkup
            </label>
          </div>
          <div className="cs_radio_wrap">
            <input
              className="cs_radio_input"
              type="radio"
              name="visit"
              id="newPatientVisit"
              value="newPatientVisit"
              onChange={handleRadioChange}
              checked={formData.visit === "newPatientVisit"}
            />
            <label className="cs_radio_label" htmlFor="newPatientVisit">
              New Patient Visit
            </label>
          </div>
          <div className="cs_radio_wrap">
            <input
              className="cs_radio_input"
              type="radio"
              name="visit"
              id="specificConcern"
              value="specificConcern"
              onChange={handleRadioChange}
              checked={formData.visit === "specificConcern"}
            />
            <label className="cs_radio_label" htmlFor="specificConcern">
              Specific Concern
            </label>
          </div>
        </div>
        <div className="cs_height_42 cs_height_xl_25" />
      </div>

      {/* Submit Button */}
      <div className="col-lg-12">
        <button
          className="cs_btn cs_style_1"
          type="submit"
          id="appointment-submit"
          name="submit"
          disabled={loading}
        >
          <span>{loading ? "Sending..." : "Submit"}</span>
          <i>
            <img src="/images/icons/arrow_white.svg" alt="Icon" />
            <img src="/images/icons/arrow_white.svg" alt="Icon" />
          </i>
        </button>
      </div>
    </form>
  );
}
